import useSWR from 'swr';
import { getConnectionPoolStats } from '../../api';
import type { ConnectionPoolStatsResponse } from './types/database';

interface UseConnectionPoolStatsReturn {
  data: ConnectionPoolStatsResponse | undefined;
  isLoading: boolean;
  isError: any;
  mutate: () => void;
}

/**
 * Hook to fetch connection pool statistics
 *
 * Returns real-time connection pool metrics including active, idle, total connections,
 * max connections, utilization percentage, and wait queue.
 *
 * @returns Connection pool stats data, loading state, error state, and mutate function
 *
 * @example
 * const { data, isLoading } = useConnectionPoolStats();
 * if (data) {
 *   console.log(`Pool utilization: ${data.connection_pool.utilization_pct}%`);
 * }
 */
export function useConnectionPoolStats(): UseConnectionPoolStatsReturn {
  const { data, error, mutate } = useSWR<ConnectionPoolStatsResponse>(
    '/admin/db-stats/connection-pool',
    getConnectionPoolStats,
    {
      refreshInterval: 30000, // Refresh every 30 seconds for real-time monitoring
      revalidateOnMount: true,
    }
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
}
